import { useEffect, useState } from "react";
import { Box, Avatar, Typography, Tab, Tabs, IconButton } from "@mui/material";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import "./inspection-firm-details.css";
import Details from "./details/details";
import Breadcrumb from "../../../../components/breadcrumb/breadcrumb";
import { useDispatch, useSelector } from "react-redux";
import { getInspectionFirmDetailsRequest } from "../../../../redux/inspection-firms/slice";
import Loading from "../../../../components/loading/loading";
import TaxDetails from "./tax-details/tax-details";
import Insurance from "./insurance/insurance";
import Services from "./services/services";
import CoverageArea from "./coverage-area/coverage-area";
import Users from "./users/users";
import ClientDocuments from "./documents/documents";
import InspectionFirmOrders from "./orders/orders";
import EditIcon from "@mui/icons-material/Edit";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import { uploadInspectionFirmLogoRequest } from "../../../../utils/APICalls";
import { useRef } from "react";
import ContactSpectrum from "../../../../components/contact-spectrum/constact-spectrum";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function TabLabel(data: any) {
  return (
    <>
      {data?.label}
      {data?.showWarning && (
        <InfoOutlinedIcon className="tab-label-warning-icon" />
      )}
    </>
  );
}

export default function InspectionFirmDetails() {
  const { state, pathname } = useLocation();
  const { inspectionFirmId } = useParams();
  const navigate = useNavigate();
  const [value, setValue] = useState(0);
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const dispatch = useDispatch();

  const { loading, inspectionFirmDetails } = useSelector(
    (state: any) => state.inspectionFirms
  );

  const firmId = inspectionFirmId || state?.id;

  useEffect(() => {
    if (firmId) {
      dispatch(getInspectionFirmDetailsRequest({ id: firmId }));
    }
  }, [firmId, dispatch]);

  useEffect(() => {
    if (pathname.includes("/users") && !pathname.includes("/users/")) {
      setValue(5);
    } else if (pathname.includes("/users/")) {
      setValue(5);
    }
  }, [pathname]);

  useEffect(() => {
    const actualFirmId = inspectionFirmId || state?.id;
    if (
      actualFirmId &&
      (state?.displayName ||
        inspectionFirmDetails?.displayName ||
        inspectionFirmDetails?.name) &&
      pathname.includes("/users") &&
      !pathname.includes("/users/")
    ) {
      const displayName =
        state?.displayName ||
        inspectionFirmDetails?.displayName ||
        inspectionFirmDetails?.name;
      if (!state?.breadcrumb?.names?.[actualFirmId]) {
        navigate(pathname, {
          state: {
            ...state,
            breadcrumb: {
              names: {
                [actualFirmId]: displayName,
              },
            },
          },
          replace: true,
        });
      }
    }
  }, [inspectionFirmId, state, inspectionFirmDetails, pathname, navigate]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    if (newValue !== 5) {
      const newPath = pathname.replace("/users", "");
      navigate(newPath, {
        replace: true,
        state: {
          breadcrumb: {
            names: {
              [inspectionFirmDetails?.id]: inspectionFirmDetails?.displayName,
            },
          },
        },
      });
    }
  };

  return (
    <>
      <ContactSpectrum />
      <Breadcrumb />
      <Box className="user-info-layout">
        <Box className="avatar-container">
          {showLoading && <Loading />}
          <Avatar
            key={firmId}
            className="user-avatar"
            alt={
              inspectionFirmDetails?.displayName || inspectionFirmDetails?.name
            }
            variant="circular"
            src={
              !loading &&
              (inspectionFirmDetails?.id === firmId ||
                !inspectionFirmDetails?.id)
                ? inspectionFirmDetails?.logoUrl
                : undefined
            }
          >
            {!inspectionFirmDetails?.logoUrl && <PersonIcon />}
          </Avatar>
          <IconButton
            className="avatar-edit-button"
            size="small"
            color="primary"
            aria-label="upload picture"
            component="label"
          >
            <input
              hidden
              accept="image/*"
              type="file"
              ref={fileInputRef}
              onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
                const file = event.target.files?.[0];
                if (file) {
                  if (file.size > 5000000) {
                    setShowErrorNotification(true);
                    setErrorMessage("File size exceeds 5MB limit.");
                    return;
                  }
                  setShowLoading(true);
                  setErrorMessage("");
                  const firmIdToUse = firmId as string;
                  const response = await uploadInspectionFirmLogoRequest(
                    firmIdToUse,
                    file
                  );
                  setShowLoading(false);
                  if (response?.status === 204) {
                    dispatch(
                      getInspectionFirmDetailsRequest({ id: firmIdToUse })
                    );
                  } else if (response?.status === 400) {
                    setShowErrorNotification(true);
                    setErrorMessage(
                      response?.data?.detail || "Failed to upload logo."
                    );
                  }
                }
              }}
            />
            <EditIcon />
          </IconButton>
        </Box>
        <Box className="margin-left">
          <Typography variant="h4">
            {inspectionFirmDetails?.displayName?.length > 40
              ? `${inspectionFirmDetails?.displayName?.slice(0, 40)}...`
              : inspectionFirmDetails?.displayName}
          </Typography>
          <Box className="user-details-content">
            {inspectionFirmDetails?.mailingAddress && (
              <Box className="user-location-content">
                <LocationOnOutlinedIcon className="user-location-icon" />
                <Typography className="user-location-txt">
                  {inspectionFirmDetails?.mailingAddress?.city},{" "}
                  {inspectionFirmDetails?.mailingAddress?.state}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {false && (
        <Box className="update-warning-layout">
          <InfoOutlinedIcon className="update-warning-icon" />
          <Box>
            <Typography className="update-warning-title">
              In Progress: New
            </Typography>
            <Typography className="update-warning-txt">
              Update all required sections in order to work with work orders.
            </Typography>
          </Box>
        </Box>
      )}

      <Box className="tab-header">
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="user details tabs"
          variant="scrollable"
          scrollButtons={false}
        >
          <Tab
            className="tab-label"
            label={<TabLabel label="Details" showWarning={false} />}
          />
          <Tab
            className="tab-label"
            label={<TabLabel label="Tax" showWarning={false} />}
          />
          <Tab
            className="tab-label"
            label={<TabLabel label="Insurance" showWarning={false} />}
          />
          <Tab
            className="tab-label"
            label={<TabLabel label="Services Offered" showWarning={false} />}
          />
          <Tab
            className="tab-label"
            label={<TabLabel label="Coverage Area" showWarning={false} />}
          />
          <Tab
            className="tab-label"
            label={<TabLabel label="Users" showWarning={false} />}
          />
          <Tab
            className="tab-label"
            label={<TabLabel label="Orders" showWarning={false} />}
          />
          <Tab
            className="tab-label"
            label={<TabLabel label="Documents" showWarning={false} />}
          />
        </Tabs>
      </Box>
      {loading && <Loading />}
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          message={errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}

      <CustomTabPanel value={value} index={0}>
        <Details data={inspectionFirmDetails} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <TaxDetails data={inspectionFirmDetails} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={2}>
        <Insurance data={inspectionFirmDetails} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={3}>
        <Services data={inspectionFirmDetails} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={4}>
        <CoverageArea data={inspectionFirmDetails} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={5}>
        <Users data={inspectionFirmDetails || state} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={6}>
        <InspectionFirmOrders data={inspectionFirmDetails || state} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={7}>
        <ClientDocuments data={inspectionFirmDetails} />
      </CustomTabPanel>
    </>
  );
}
